import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @callback onChange
 * @param {boolean} isChecked - True if the checkbox is checked, otherwise
 *    false.
 */

/**
 * A switch UI component for a single checkbox.
 *
 * @param {object} props - Props.
 * @param {string} props.id - ID.
 * @param {string} props.label - Label.
 * @param {boolean} props.isChecked - True if checkbox is checked, otherwise
 *    false.
 * @param {onChange} props.onChange - Callback function executed when the
 *    checked state of the checkbox changes.
 * @param {boolean} [props.labelRightPosition] - True to show label right, otherwise left.
 * @returns {React.Element} Component.
 */

export const SwitchInput = (props) => {
    const [isChecked, setIsChecked] = useState(props.isChecked);
    const switchInputRef = useRef();

    const onChange = (checked) => {
        setIsChecked(checked);
        props.onChange(checked);
    };

    const onClick = () => {
        switchInputRef?.current.dispatchEvent(
            new Event(PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE, {
                bubbles: true
            })
        );
    };

    useLayoutEffect(() => {
        onChange(props.isChecked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isChecked]);

    return (
        <div
            className={classNames('switch-input', {
                'switch-input--checked': isChecked,
                'switch-input--label-right': props.labelRightPosition
            })}
            ref={switchInputRef}
        >
            <label
                className="switch-input__label"
                htmlFor={props.id}
                tabIndex={0}
                onKeyPress={(evt) => {
                    if ([' ', 'Enter'].includes(evt.key)) {
                        evt.preventDefault();
                        onChange(!isChecked);
                    }
                }}
            >
                {props.labelRightPosition ? null : props.label}

                <input
                    className="switch-input__input"
                    type="checkbox"
                    id={props.id}
                    checked={isChecked}
                    onChange={(evt) => onChange(evt.target.checked)}
                    onClick={onClick}
                />

                {props.labelRightPosition ? props.label : null}
            </label>
        </div>
    );
};

SwitchInput.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    isChecked: PropTypes.bool,
    onChange: PropTypes.func,
    labelRightPosition: PropTypes.bool
};
